<template>
	<div class="wrapper">
		<div class="murk">
			<BalanceInfo :title="$t('checkout.title')" is-in-game />

			<div class="ingame-order" :class="{ 'use-mileage': isUseMileage }">
				<div v-if="isDisplayBalance" class="ingame-order__balance-wrapper">
					<div v-if="isExceedTransactionLimit" class="ingame-order__warning">
						<i18n path="checkout.order-exceeds-max" tag="div">
							<template v-slot:maxAmount>
								<currency
									v-if="transactionLimit"
									:code="transactionLimit.currency"
									:value="transactionLimit.amount"
								/>
							</template>
						</i18n>
					</div>

					<div v-if="isDisplayBalance" class="ingame-order__balance">
						{{ $t('checkout.your-current-balance-is') }}
						<currency
							class="ingame-order__balance__value"
							:code="totalBalanceCurrency"
							:value="totalBalance"
						/>
					</div>
				</div>

				<div v-if="!isUseMileage" class="ingame-order__title">
					{{ $t('checkout.order-details') }}
				</div>

				<div class="ingame-order__items">
					<div class="ingame-order__items__wrapper">
						<div v-if="isUseMileage" class="ingame-order__items__title">
							{{ $t('checkout.order-details') }}
						</div>
						<div class="ingame-order__items__item ingame-order__items__item--title">
							<div class="ingame-order__items__item__name">{{ $t('item') }}</div>
							<div class="ingame-order__items__item__qty">{{ $t('checkout.qty') }}</div>
							<div class="ingame-order__items__item__value">{{ $t('checkout.price') }}</div>
						</div>

						<div v-for="(item, index) in orderItems" :key="index" class="ingame-order__items__item">
							<div class="ingame-order__items__item__name" :title="item.name">
								<span class="ingame-order__items__item__name__title">
									{{ $t('item') }}
								</span>
								<span class="ingame-order__items__item__name__value">{{ item.name }}</span>
							</div>
							<div class="ingame-order__items__item__qty">
								<span class="ingame-order__items__item__qty__title">
									{{ $t('checkout.qty') }}
								</span>
								<span class="ingame-order__items__item__qty__value">{{ item.quantity }}</span>
							</div>
							<div class="ingame-order__items__item__value">
								<span class="ingame-order__items__item__value__title">
									{{ $t('checkout.price') }}
								</span>
								<span class="ingame-order__items__item__value__value"
									><currency :code="item.currency" :value="item.amount"
								/></span>
							</div>
							<div class="separator" />
						</div>
					</div>
					<div v-if="isUseMileage" class="ingame-order__items__mileage-wrapper">
						<InGameMileage
							:cancel-loading="cancelLoading"
							:continue-loading="continueLoading"
							@cancel="() => cancelAction('mileage')"
							@continue="mileageContinuePurchase"
						/>
					</div>
				</div>
				<div v-if="!isUseMileage" class="ingame-order__total">
					{{ $t('checkout.order-total') }}
					<currency
						class="ingame-order__total__value"
						:class="{ 'ingame-order__total__value--exceed': isExceedTransactionLimit }"
						:code="orderCurrency"
						:value="orderTotal"
					/>
				</div>

				<div
					v-if="paymentInfo && paymentInfo.isUseWallet && !isExceedTransactionLimit"
					class="ingame-order__info"
				>
					<div class="ingame-order__info__row">
						<div class="ingame-order__info__row__title">
							{{ $t('checkout.available-balance') }}
						</div>
						<div class="ingame-order__info__row__value">
							<currency :code="paymentInfo.currency" :value="paymentInfo.balance" />
						</div>
					</div>

					<div class="ingame-order__info__row">
						<div class="ingame-order__info__row__title">{{ $t('checkout.purchase-amount') }}</div>
						<div class="ingame-order__info__row__value">
							<currency :code="paymentInfo.currency" :value="paymentInfo.orderTotal" />
						</div>
					</div>

					<div
						class="ingame-order__info__row"
						:class="{ 'ingame-order__info__row--amount-due': paymentInfo.hasAmountDue }"
					>
						<div class="ingame-order__info__row__title">
							{{ $t(paymentInfo.hasAmountDue ? 'checkout.amount-due' : 'checkout.balance-updated') }}
						</div>
						<div class="ingame-order__info__row__value">
							<template v-if="paymentInfo.hasAmountDue">
								<span>({{ $t('checkout.insufficient-balance') }})</span>
								<currency :code="paymentInfo.currency" :value="paymentInfo.amountDue" />
							</template>
							<currency v-else :code="paymentInfo.currency" :value="paymentInfo.updatedBalance" />
						</div>
					</div>
				</div>

				<div v-if="requirePricePointSelection" class="ingame-order__pricepoints">
					<div class="ingame-order__pricepoints__title">{{ $t('checkout.select-amount') }}</div>

					<FormSelect
						id="ingame-pricepoints"
						v-model="$v.selectedPricepoint.$model"
						:options="displayedPricePoints"
						:has-error="$v.selectedPricepoint.$error"
						:options-value="'productId'"
						:options-label="'name'"
						:place-holder="$t('checkout.select-amount')"
						dont-localize
					/>
				</div>

				<div
					v-if="paymentInfo && paymentInfo.isUseWallet && !isExceedTransactionLimit"
					class="ingame-order__help"
				>
					<div>{{ $t('checkout.ingame.balance-update-moment') }}</div>
					<div v-if="requirePayment && region === 'global'">
						{{ $t('checkout.additional-tax-may-apply') }}
					</div>
					<i18n path="checkout.for-information-billing" tag="div">
						<template v-slot:nexonSupportCenter>
							<a :href="supportLink" target="_blank" @click="supportTracking">{{
								$t('checkout.nexon-support-center')
							}}</a>
						</template>
					</i18n>
				</div>

				<div v-if="!isUseMileage" class="ingame-order__actions">
					<div class="ingame-order__actions__btn-wrapper">
						<FormButton
							class="btn-primary"
							:label="$t('cancel')"
							:button-click="cancelAction"
							:disabled="continueLoading"
							:loading="cancelLoading"
						/>
					</div>
					<div v-if="!isExceedTransactionLimit" class="ingame-order__actions__btn-wrapper">
						<FormButton
							:label="$t('checkout.continue-purchase')"
							:button-click="continuePurchase"
							:disabled="$v.$invalid || cancelLoading"
							:loading="continueLoading"
						/>
					</div>
				</div>
				<br />
				<div class="summary__message">
					<p v-if="false && region === 'global'" class="summary__message__v4">
						<i18n
							class="summary__message__terms_link"
							path="checkout.digital-license-summary-notice"
							tag="p"
						>
							<template v-slot:termsLink>
								<a
									v-t="'checkout.digital-license-summary-terms-link'"
									:href="tobenDigitalLicenseTerms"
									target="_blank"
								></a>
							</template>
						</i18n>
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BalanceInfo from '@/components/common/balance-info';
import currency from '@/components/common/currency';
import { FormSelect, FormButton } from '@arena/toolkit';
import store from '@/store';
import { requiredIf } from 'vuelidate/lib/validators';
import links from '@/config/links';
import InGameMileage from './mileage';

export default {
	name: 'InGameOrder',
	components: {
		BalanceInfo,
		currency,
		FormButton,
		FormSelect,
		InGameMileage,
	},
	data() {
		return {
			selectedPricepoint: null,
			pricePoints: [],
			cancelLoading: false,
			continueLoading: false,
			mileageAmount: 0,
			paidAmount: 0,
		};
	},
	validations() {
		return {
			selectedPricepoint: {
				required: requiredIf(() => this.requirePricePointSelection),
			},
		};
	},
	computed: {
		inGameOrderData() {
			return this.$store.state.inGameOrderData;
		},
		requirePricePointSelection() {
			return this.inGameOrderData.requirePricePointSelection;
		},
		isUseMileage() {
			return this.paymentInfo.isUseMileage;
		},
		displayedPricePoints() {
			return this.inGameOrderData.pricePoints.map((item) => {
				let name;

				if (item.productType === 'nxwallet') {
					name = `${this.$n(item.value)} NX / ${this.$n(item.amount, 'currency', item.currency)}`; // NX case, display: value NX / amount USD
				} else if (item.currency === 'TWD') {
					name = `${this.$n(item.amount)} ${this.$t('points')} / ${this.$n(
						item.amount,
						'currency',
						item.currency
					)}`; // TW case, display: amount Points / amount TWD
				} else {
					name = this.$n(item.amount, 'currency', item.currency);
				}
				return {
					productId: item.productId,
					name: name,
				};
			});
		},
		orderCurrency() {
			return this.inGameOrderData.order.currency;
		},
		orderItems() {
			return this.inGameOrderData.order.items;
		},
		orderTotal() {
			return this.inGameOrderData.order.total;
		},
		languageCode() {
			return this.$i18n.locale;
		},
		requirePayment() {
			return this.inGameOrderData.requirePayment;
		},
		checkoutId() {
			return this.inGameOrderData.checkoutId;
		},
		paymentInfo() {
			return this.inGameOrderData.payment;
		},
		totalBalance() {
			return this.$store.state.accountBalance.total;
		},
		totalBalanceCurrency() {
			return this.$store.state.accountBalance.currency;
		},
		isDisplayBalance() {
			return this.$store.state.accountBalance.isDisplayBalance;
		},
		isExceedTransactionLimit() {
			return this.inGameOrderData.isExceedTransactionLimit;
		},
		transactionLimit() {
			return this.inGameOrderData.transactionLimit;
		},
		supportLink() {
			return links[this.region].supportLink;
		},
		isSandboxMode() {
			return this.$store.state.sandboxMode;
		},
	},
	mounted() {
		this.$cookie.set('gameCode', `Arena_gamecodetype:${this.inGameOrderData.order.serviceCode}`);
		this.$cookie.set('gameCodeType', 'serviceCode');
		this.$a2s.view('imp');
		if (this.isExceedTransactionLimit) {
			dataLayer.push({
				event: 'In Game Purchase Over Limit',
				IGP_amount: this.orderTotal,
				IGP_currency: this.orderCurrency,
				IGP_sufficient: !this.requirePayment,
			});
		}
	},
	methods: {
		async cancelAction(type) {
			let object = 'cancel_purchase';
			if (type === 'mileage') {
				object = 'mileage_cancel_purchase';
			}
			this.$a2s.click(object);
			try {
				this.cancelLoading = true;
				const result = await this.$checkoutAPI.cancelInGameTransaction(this.checkoutId);
				if (this.$store.state.displayCallbackIframe) window.close();
				else window.location.replace(result.data.redirectUrl);
			} catch (error) {
				if (this.$store.state.displayCallbackIframe) return window.close();

				const errorCode = error.response && error.response.data && error.response.data.code;
				this.cancelLoading = false;

				this.$router.replace({
					name: 'in-game-purchase-failed',
					query: {
						...(errorCode && { error_code: errorCode }),
					},
				});
			}
		},
		async continuePurchase(type) {
			let object = 'continue_purchase';
			if (type === 'mileage') {
				object = 'mileage_continue_purchase';
			}
			this.$a2s.click(object, {
				objectTag: 'purchase_start',
			});
			this.$v.$touch();

			if (!this.$v.$invalid) {
				try {
					this.continueLoading = true;

					if (this.requirePayment) {
						// insuficient balance or direct payment case
						let igpAmount;
						if (
							this.requirePricePointSelection &&
							this.inGameOrderData.pricePoints &&
							this.selectedPricepoint
						) {
							this.inGameOrderData.pricePoints.find((item) => {
								if (item.productId === this.selectedPricepoint) {
									if (this.region === 'global') {
										igpAmount = item.value;
									} else {
										igpAmount = item.amount;
									}
								}
								return item.productId === this.selectedPricepoint;
							});
						} else {
							igpAmount = this.orderTotal;
						}
						dataLayer.push({
							event: 'In Game Purchase Confirm',
							IGP_amount: igpAmount,
							IGP_currency: this.orderCurrency,
							IGP_sufficient: false,
						});

						const response = await this.$checkoutAPI.createCheckoutInGameToken(
							this.checkoutId,
							this.languageCode,
							this.selectedPricepoint,
							this.isUseMileage ? this.mileageAmount : null
						);
						this.$store.commit('setToken', response.data.token);
						if (response.data.skipPage) {
							this.$store.commit('setSelectedPg', {
								channelNo: response.data.channelNo,
								iframe: false,
							});
							await this.$store.dispatch('beginPayment');
							return;
						}

						this.selectedPricepoint = null;
						this.continueLoading = false;

						this.$router.replace({
							name: 'in-game-purchase',
							query: {
								...(this.isSandboxMode ? { sandbox: 'true' } : {}),
							},
						});
					} else {
						// suficient balance case, call the API for getting the result and redirect to the result page (in this case user skip PG selection and payment process)
						dataLayer.push({
							event: 'In Game Purchase Confirm',
							IGP_amount: this.orderTotal,
							IGP_currency: this.orderCurrency,
							IGP_sufficient: true,
						});

						await this.$checkoutAPI.completeInGameTransaction(this.checkoutId);
						this.continueLoading = false;

						this.$router.replace({
							name: 'in-game-result',
							query: {
								status: this.$store.state.inGameTransactionResult.status, // status will be 'complete' all the time for this sufficient case
							},
						});
					}
				} catch (error) {
					if (error.display && error.display === 'fetchBillingInfoVerifyToken') {
						return;
					}

					this.continueLoading = false;
					const errorCode = error.response && error.response.data && error.response.data.code;

					this.$router.replace({
						name: 'in-game-purchase-failed',
						query: {
							...(errorCode && { error_code: errorCode }),
							sufficient: !this.requirePayment,
						},
					});
				}
			}
		},
		async mileageContinuePurchase(mileageAmount, paidAmount) {
			this.mileageAmount = mileageAmount;
			this.paidAmount = paidAmount;

			this.continuePurchase('mileage');
		},
		supportTracking() {
			dataLayer.push({
				event: 'In Game Purchase Support Click',
			});
		},
	},
	async beforeRouteEnter(to, from, next) {
		const token = to.query.token,
			serviceType = to.query.service_type,
			callbackIframe = to.query.callback_iframe;
		try {
			if (callbackIframe && callbackIframe === 'true') store.commit('setDisplayCallbackIframe', true);
			else store.commit('setDisplayCallbackIframe', false);

			const order = await store.dispatch('fetchVerifyInGameToken', { token, serviceType });
			if (order.data.payment.isUseMileage) {
				await store.dispatch('fetchMileageBalance');
			}
			next((vm) => {
				if (order.data.pricePoints) vm.pricePoints = order.data.pricePoints;
			});
		} catch (error) {
			const errorCode = error.response && error.response.data && error.response.data.code;
			next({
				name: 'in-game-purchase-failed',
				replace: true,
				params: to.params,
				query: {
					...(errorCode && { error_code: errorCode }),
					sufficient: 'unknown',
				},
			});
		}
	},
};
</script>

<style lang="scss" scoped>
.ingame-order {
	max-width: 640px;
	margin: 0 auto;

	$self: &;
	$bgDark: rgba(#000, 0.5);

	&__balance-wrapper {
		background-color: $bgDark;
		border: 2px solid #8f8f8f;
		margin-top: 40px;
		> div {
			&:first-child {
				padding-bottom: 10px;
			}
			&:last-child {
				padding-bottom: 15px;
			}
		}
	}

	&__warning {
		font-size: 14px;
		line-height: 18px;
		font-weight: bold;
		color: #ff1744;
		text-align: center;
		padding: 14px 20px 15px 20px;

		+ #{$self}__balance {
			padding-top: 0px;
		}
	}

	&__balance {
		font-size: 16px;
		line-height: 20px;
		padding: 14px 20px 15px 20px;
		text-align: center;
		color: #fff;
		&--warning {
			margin-top: 0px;
			padding-top: 10px;
		}
		&__value {
			color: #eda03b;
			padding-left: 6px;
			font-weight: bold;
			@media (max-width: 600px) {
				display: block;
				padding-left: 0px;
			}
		}
	}

	&__title {
		font-size: 18px;
		font-weight: bold;
		color: #fff;
		padding-top: 37px;
	}

	&__items {
		&__wrapper {
			display: table;
			min-width: 100%;
		}

		margin-top: 40px;
		border-top: 2px solid #8f8f8f;
		border-bottom: 2px solid #8f8f8f;
		color: #fff;

		&__item {
			$self: &;

			&#{$self}--title {
				display: none;
			}
		}
		@media (max-width: 699px) {
			&__item {
				font-size: 16px;
				line-height: 18px;
				min-width: 100%;
				display: table;

				&__name,
				&__qty,
				&__value {
					display: table-row;
					&__title {
						width: 1px;
						white-space: nowrap;
						padding-right: 10px;
					}
					> span {
						display: table-cell;
						padding-top: 5px;
						padding-bottom: 6px;
					}
				}

				&__name {
					> span {
						padding-top: 15px;
					}
				}

				&__value {
					> span {
						padding-bottom: 16px;
					}
				}
			}
		}
		@media (min-width: 700px) {
			display: table;
			font-size: 16px;
			line-height: 24px;
			min-width: 100%;
			padding-top: 10px;
			padding-bottom: 9px;

			&__item {
				display: table-row;
				$self: &;

				&#{$self}--title {
					display: table-row;
					font-size: 14px;
					line-height: 22px;
					font-weight: normal;
				}

				&__name,
				&__qty,
				&__value {
					display: table-cell;
					padding-top: 2px;
					padding-bottom: 4px;
					&__title {
						display: none;
					}
				}
				&__name {
					width: 1px;
					max-width: 250px;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					max-width: 424px;
				}
				&__qty {
					padding-left: 20px;
					padding-right: 10px;
					width: 1px;
					white-space: nowrap;
					text-align: center;
				}
				&__value {
					text-align: right;
					white-space: nowrap;
				}
			}
		}
	}

	&__total {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		flex: 0 0 100%;
		max-width: 100%;
		font-size: 16px;
		padding-top: 17px;
		padding-bottom: 18px;
		color: #fff;
		&__value {
			margin-left: auto;
			&--exceed {
				color: #ff1744;
			}
		}
	}

	&__info {
		padding: 9px 20px 11px 20px;
		min-width: 100%;
		margin-bottom: 14px;
		background-color: $bgDark;
		&__row {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;
			font-size: 14px;
			$self: &;
			&__title,
			&__value {
				padding-top: 7px;
				padding-bottom: 7px;
			}
			&__title {
				margin-right: auto;
				padding-right: 18px;
				color: #fff;
			}
			&#{$self}--amount-due {
				#{$self}__value {
					color: #ff1744;
				}
			}
			&__value {
				font-weight: bold;
				color: #eda03b;
				margin-left: auto;
				> {
					:last-child {
						padding-left: 5px;
					}
				}
			}

			@media (max-width: 600px) {
				&__title,
				&__value {
					flex: 0 0 100%;
					max-width: 100%;
					text-align: center;
					padding-left: 0px;
					padding-right: 0px;
				}
				&__title {
					padding-bottom: 6px;
				}
				&__value {
					padding-top: 0px;
					> span {
						display: block;
						&:nth-child(2) {
							padding-top: 3px;
						}
					}
				}
			}
		}
	}

	&__help {
		text-align: center;
		font-size: 14px;
		line-height: 18px;
		padding-top: 14px;
		padding-bottom: 16px;
		a {
			color: inherit;
			text-decoration: underline;
			text-underline-position: under;
			&:hover {
				color: #fff;
			}
		}
	}
	&__pricepoints {
		padding-top: 3px;
		padding-bottom: 20px;
		margin-left: auto;
		margin-right: auto;
		&__title {
			font-size: 18px;
			font-weight: bold;
			color: #fff;
			margin-bottom: 19px;
		}
		::v-deep .form-group {
			margin-bottom: 0;
			> label {
				display: none;
			}
			.form-control {
				position: relative;
				.caret {
					top: 50%;
				}
			}
			.dropdown-menu {
				max-height: 246px;
			}
		}
	}
	&__actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		&__btn-wrapper {
			flex: 0 0 50%;
			max-width: 50%;
			padding: 0 10px;
			button {
				margin-top: 20px;
				&.disabled {
					&:active {
						color: #262626;
						background-color: #fff;
						border-color: #d9d9d9;
					}
				}
				&.disabled.btn-primary {
					&:active,
					&:hover {
						color: #fff;
						background-color: #4d4d4d;
						border-color: #4d4d4d;
					}
				}
			}
			&:first-child {
				padding-left: 0px;
			}
			&:last-child {
				padding-right: 0px;
			}
		}
	}
}

@media (max-width: 700px) {
	.ingame-order {
		&__help {
			> div {
				display: inline;
				padding-left: 1px;
				padding-right: 1px;
			}
		}
		&__actions {
			&__btn-wrapper {
				flex: 0 0 100%;
				max-width: 100%;
				padding: 0px;
			}
		}
		&__balance-wrapper {
			margin-top: 20px;
		}
	}
}

.use-mileage {
	&.ingame-order {
		@media (min-width: 1024px) {
			max-width: none;
			margin-top: 50px;
		}
	}

	.ingame-order {
		&__items__wrapper {
			min-width: unset;
			display: block;
			@media (min-width: 1024px) {
				flex-basis: 50%;
				border-top: 2px solid #8f8f8f;
			}
		}

		&__items {
			border-top: none;
			border-bottom: none;

			&__title {
				font-size: 18px;
				font-weight: 700;
				padding-top: 12px;
				margin-bottom: 16px;
				border-top: 2px solid #8f8f8f;
			}

			&__mileage-wrapper {
				margin-top: 28px;
			}

			@media (min-width: 700px) {
				display: block;
				font-size: 14px;
				padding-top: 0px;
				padding-bottom: 0px;
				line-height: 18px;
			}

			@media (min-width: 1024px) {
				display: flex;
				align-items: flex-start;
				gap: 22px;

				&__mileage-wrapper {
					margin-top: 0px;
					flex-basis: 50%;
				}

				&__title {
					border-top: none;
				}
			}

			&__item {
				padding: 16px 16px 16px 16px;
				background-color: rgba(255, 255, 255, 0.05);
				border-radius: 1px;
				font-size: 14px;
				font-weight: 400;
				opacity: 1;
				color: white;

				&:not(:last-child) {
					.separator {
						position: relative;
						top: 16px;
						height: 1px;
						margin-top: 4px;
						margin-bottom: 4px;
						background-color: rgba(255, 255, 255, 0.05);
					}
				}

				@media (min-width: 700px) {
					display: block;
					line-height: 18px;
					&--title {
						display: none;
					}
				}

				&__qty,
				&__value,
				&__name {
					display: flex;
					width: 100%;
					justify-content: space-between;
					align-items: center;
					padding-top: 0;
					padding-bottom: 0;

					@media (min-width: 700px) {
						&__title {
							width: 1px;
							white-space: nowrap;
							padding-right: 10px;
						}
						> span {
							display: block;
							padding-top: 5px;
							padding-bottom: 6px;
						}
					}

					&__title {
						opacity: 0.5;
						padding: 0;
					}

					&__value {
						opacity: 0.8;
						padding: 0;
					}
				}

				&__qty {
					@media (min-width: 700px) {
						text-align: left;
						padding-left: 0;
						padding-right: 0;
					}
				}

				&__name {
					@media (min-width: 700px) {
						max-width: none;
					}
				}

				&__value__value {
					opacity: 1;
				}

				&__name__value {
					max-width: 85%;
					white-space: wrap;

					@media (max-width: 1024px) {
						max-width: 90%;
					}

					@media (max-width: 610px) {
						max-width: 87%;
					}

					@media (max-width: 480px) {
						max-width: 85%;
					}
				}
			}
		}
	}
}
</style>
